import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import type { SubStepInstance } from '@ebalance/proto/bffuser/v1/workflow_case_model_pb'
import { ToggleSubStepRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { useAuth } from '@enechain/auth-js'
import {
  Alert,
  Box,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { localizeBuiltinWorkflowText } from '~/models/workflowBuiltinText'
import { useToggleSubStep } from '~/pages/WorkflowCaseDetail/hooks/useToggleSubStep'
import { resolveApproverAllInactiveMessage } from '~/pages/WorkflowCaseDetail/models/serverError'
import { routePaths } from '~/routes/routePaths'

type Props = {
  isOpen: boolean
  caseId: string
  /** 閲覧者が担当者に含まれない (spec AC-WFW-007e (b)): 確認文言を出す。 */
  isAssigneeNotApplicable: boolean
  bigStepDisplayOrder: number
  subStep: SubStepInstance
  onClose: () => void
}

/**
 * 汎用サブステップ完了確認ダイアログ (design/demand/case-detail.html `#cd-gc-modal` 相当)。
 *
 * チェックボックスの ON 操作をこのダイアログでインターセプトし、実施者 (readonly) を
 * 確認した上で完了 mutation (`useToggleSubStep`) を実行する。キャンセル / × / backdrop /
 * Esc では mutation を呼ばない (= controlled checkbox のため、呼ばなければチェックは
 * 入らないまま維持される。design の「チェックを戻す」JS ロジックに相当する処理は不要)。
 *
 * メモ入力欄は実装しない: design にはあるが `SubStepInstance` proto に memo フィールドが
 * 無く保存先が存在しないため、黙って入力を捨てる UI は作らない判断 (完了報告に明記)。
 * 「確定登録」専用ダイアログ (`#cd-substep-complete-modal`、エリア確定状況チェック) は
 * 別 backend gap のため対象外 (承認待ち / 自動スキップ行はチェック操作不可のまま維持)。
 */
export const SubStepCompleteDialog = ({
  isOpen,
  caseId,
  isAssigneeNotApplicable,
  bigStepDisplayOrder,
  subStep,
  onClose,
}: Props): React.ReactElement => {
  const showSuccessToast = useSuccessToast()
  const { t } = useTranslation()
  const { user } = useAuth()
  const { mutateAsync: toggleAsync, isPending } = useToggleSubStep(caseId)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )
  // AC-WFW-007 (issue #5341, workflow-approver-all-inactive-block Design
  // Note): 承認者全員無効の拒否は他の失敗と違い、承認フロー設定画面への誘導
  // リンクを添えた custom 表示にする (通常の completeFailed/aborted Alert とは
  // 別枠)。
  const [approverAllInactiveMessage, setApproverAllInactiveMessage] = useState<
    string | undefined
  >(undefined)

  const userName = user?.name ?? ''

  const handleConfirm = async (): Promise<void> => {
    setErrorMessage(undefined)
    setApproverAllInactiveMessage(undefined)
    try {
      await toggleAsync(
        create(ToggleSubStepRequestSchema, {
          workflowCaseId: caseId,
          bigStepDisplayOrder,
          subStepDisplayOrder: subStep.displayOrder,
          isCompleted: true,
          // completedBy は送らない (workflow-substep-assignee design):
          // proto field は reserved 化済み、 BFF handler が eauth の member_id を注入する。
        }),
      )
      onClose()
      // design L2462: 完了確定は modal submit のこのタイミングで toast (checkbox check 時点では出さない)
      showSuccessToast({
        title: t('page:workflowCaseDetail.subStep.completeToast'),
      })
    } catch (err) {
      if (err instanceof ConnectError) {
        // AC-WFW-007: 承認者全員無効 (要承認・段階未設定 or 設定済承認者が
        // 全員無効) は誘導ダイアログ表示。reason が解決できなければ既存の
        // aborted/completeFailed fallback へ。
        const guidance = resolveApproverAllInactiveMessage(err, t)
        if (guidance !== undefined) {
          setApproverAllInactiveMessage(guidance)
          return
        }
        if (err.code === Code.Aborted) {
          setErrorMessage(t('page:workflowCaseDetail.error.aborted'))
          return
        }
      }
      setErrorMessage(t('page:workflowCaseDetail.error.completeFailed'))
    }
  }

  const footer = (
    <SaveCancelFooter
      isPending={isPending}
      onCancel={onClose}
      onSave={() => {
        void handleConfirm()
      }}
      saveLabel={t('page:workflowCaseDetail.subStep.completeDialog.confirm')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={t('page:workflowCaseDetail.subStep.completeDialog.title', {
        name: localizeBuiltinWorkflowText(subStep.name, t),
      })}
    >
      <VStack align="stretch" gap={3}>
        {approverAllInactiveMessage !== undefined && (
          <Alert status="error" title={approverAllInactiveMessage}>
            {/* 直す画面への導線は本文の下の文字リンクでなく、 Alert の右端の Tertiary sm
                (UI 辞書 components/alert-placement.md §4)。 新しいタブで開く
                (完了の確認中のダイアログから別の管理画面へ移るため。 link-target.md の別途指定) */}
            <AlertActions>
              <TertiaryButton asChild size="sm">
                <RouterLink
                  rel="noopener noreferrer"
                  target="_blank"
                  to={routePaths.adminApprovalFlow.path}
                >
                  {t(
                    'page:workflowCaseDetail.subStep.completeDialog.approverAllInactive.linkLabel',
                  )}
                </RouterLink>
              </TertiaryButton>
            </AlertActions>
          </Alert>
        )}
        {errorMessage !== undefined && (
          <Alert status="error" title={errorMessage} />
        )}
        {isAssigneeNotApplicable && (
          <Alert
            status="warning"
            title={t(
              'page:workflowCaseDetail.subStep.completeDialog.notAssigneeConfirm',
            )}
          />
        )}
        <VStack align="stretch" gap={1}>
          <Text textStyle="label" color="Text.highEmphasis">
            {t('page:workflowCaseDetail.subStep.completeDialog.executor')}
          </Text>
          <Box
            padding="8px 12px"
            background="Surface.secondary"
            borderRadius="base"
          >
            <Text color="Text.highEmphasis" fontSize="md">
              {t(
                'page:workflowCaseDetail.subStep.completeDialog.executorValue',
                { name: userName },
              )}
            </Text>
          </Box>
        </VStack>
      </VStack>
    </Modal>
  )
}
